import { Box, LinearProgress } from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import {
    Applications,
    RoutePaths,
    USER_ROLES,
    UserPermissions,
} from '../../../constants/constants'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import {
    CustomTabs,
    CustomTab,
    a11yProps,
    CustomTabPanel,
} from '../../Common/MuiTabs'
import Dashboard from '../../Common/Dashboard'
import { useTranslation } from 'react-i18next'
import { DEVICE_MANAGER_TABS } from './constants'
import DeviceSettings from './Settings'
import {
    getApplicationSettings,
    ISettingsResponse,
} from '../../../services/applications'
import { DeviceManagerSettingsType } from './types'
import { updateDeviceManagerSettings } from '../../../redux/slices/deviceManagerSlice'
import { useNavigate } from 'react-router-dom'
import DeviceManagerListView from './ListView'
import { usePermissions } from '../../../hooks/usePermissions'

const DeviceManager = (): ReactElement => {
    const { t } = useTranslation()
    const org = useAppSelector((state) => state.org.org)
    const loadingPermissions = useAppSelector(
        (state) => state.org.loadingPermissions,
    )
    const deviceManagerSettings = useAppSelector(
        (state) => state.deviceManager.deviceManagerSettings,
    )
    const userRole = useAppSelector((state) => state.user.user?.role) ?? ''
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { hasPermission } = usePermissions()
    const hasPermissionAccess = useMemo(
        () =>
            hasPermission([
                UserPermissions.ORG_ADMIN,
                UserPermissions.SYSTEM_READER,
            ]) || userRole === USER_ROLES.DEVICE_MANAGER,
        [hasPermission, userRole],
    )
    const hasWriteAccess = useMemo(
        () =>
            hasPermission([
                UserPermissions.ORG_ADMIN,
                // UserPermissions.DEVICE_WRITER,
            ]),
        [hasPermission],
    )

    const [tabIndex, setTabIndex] = useState<number | null>(null)

    const [isLoadingSettings, setIsLoadingSettings] = useState(true)

    const [enableDeviceManager, setEnableDeviceManager] = useState(false)

    useEffect(() => {
        if (deviceManagerSettings?.enable) {
            setEnableDeviceManager(true)
        } else {
            setEnableDeviceManager(false)
        }
    }, [deviceManagerSettings])

    useEffect(() => {
        const getSettings = async () => {
            setIsLoadingSettings(true)
            const inTransitAppSettingsResponse: ISettingsResponse<DeviceManagerSettingsType> =
                await getApplicationSettings<DeviceManagerSettingsType>(
                    org?.id || '',
                    Applications.deviceManager.id,
                )
            const settings = inTransitAppSettingsResponse?.appSettings || {}
            if (settings?.enable) {
                setTabIndex(DEVICE_MANAGER_TABS.LIST_VIEW)
                setEnableDeviceManager(true)
            } else if (hasPermissionAccess) {
                setTabIndex(DEVICE_MANAGER_TABS.SETTINGS)
            } else {
                setTabIndex(null)
                navigate(RoutePaths.Home)
            }

            dispatch(updateDeviceManagerSettings(settings))
            setIsLoadingSettings(false)
        }

        if (loadingPermissions) {
            /**
             * After the change of organization, we fetch latest permissions.
             * Till we fetch the latest permissions, we should wait here.
             * Because in the latest permissions only we will have updated showSettings value
             */
            return
        }
        if (org?.id && !loadingPermissions) {
            getSettings()
        }
    }, [org, loadingPermissions, hasPermissionAccess, navigate, dispatch])

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue)
    }

    if (loadingPermissions || isLoadingSettings) {
        return <LinearProgress />
    }

    if (tabIndex === null) {
        return <Box></Box>
    }

    return (
        <>
            <CustomTabs
                value={tabIndex}
                onChange={handleChange}
                aria-label={t('basictabs')}
                TabIndicatorProps={{
                    children: <span />,
                }}
            >
                <CustomTab
                    label={t('dashboard')}
                    {...a11yProps(DEVICE_MANAGER_TABS.DASHBOARD)}
                    disabled={!enableDeviceManager}
                />
                <CustomTab
                    label={t('deviceView')}
                    {...a11yProps(DEVICE_MANAGER_TABS.LIST_VIEW)}
                    disabled={!enableDeviceManager}
                />
                {hasPermissionAccess && (
                    <CustomTab
                        label={t('settings')}
                        {...a11yProps(DEVICE_MANAGER_TABS.SETTINGS)}
                    />
                )}
            </CustomTabs>
            <CustomTabPanel
                value={tabIndex}
                index={DEVICE_MANAGER_TABS.DASHBOARD}
            >
                <Dashboard analyticsDashboardId={''} />
            </CustomTabPanel>
            <CustomTabPanel
                value={tabIndex}
                index={DEVICE_MANAGER_TABS.LIST_VIEW}
            >
                <DeviceManagerListView />
            </CustomTabPanel>
            <CustomTabPanel
                value={tabIndex}
                index={DEVICE_MANAGER_TABS.SETTINGS}
            >
                <DeviceSettings hasWriteAccess={hasWriteAccess} />
            </CustomTabPanel>
        </>
    )
}

export default DeviceManager
